import { useEffect, useState } from 'react';
import { formatMoney, TERMS_VERSION, type BillingLink, type MeResponse, type MyBilling, type RedeemedCredit } from '@hearth/shared';
import type { Api } from '../api';
import { linkTo } from '../router';
import { errorText } from './serverWords';

// /account/billing (B5, as the canvas board, wording approved 2026-10-27): the payment method monthly
// invoices charge (added on Stripe's Checkout page, changed in Stripe's customer portal), this month so
// far, past invoices (the portal), and a banner while an invoice is unpaid. Free credit (the canvas's
// "Free credit" boards, owner's wording): nothing about it shows to someone without any, only a code
// box; redeeming one shows the credit card, spent before any card is charged.

/** Below this (cents), the credit card says it's running low. */
const CREDIT_LOW_CENTS = 200;

/** Cents as money: 1500 → $15.00. */
const cents = (amount: number) => formatMoney(amount * 100);

/** "Visa ending 4242", "Link · sam@example.com", or the method's type. */
export function methodText(method: NonNullable<MyBilling['paymentMethod']>): string {
  if (method.type === 'card') {
    const brand = method.brand ? method.brand[0]!.toUpperCase() + method.brand.slice(1) : 'Card';
    return method.last4 ? `${brand} ending ${method.last4}` : brand;
  }
  if (method.type === 'link') return method.email ? `Link · ${method.email}` : 'Link';
  return method.type;
}

/** The short mark beside it: VISA, LINK, CARD. */
const chipText = (method: NonNullable<MyBilling['paymentMethod']>) => (method.type === 'card' ? (method.brand ?? 'card') : method.type).slice(0, 4).toUpperCase();

/** "October" (UTC month). */
const monthOnly = (month: string) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });

/** "1 November": the next month's first day, when this one is billed. */
function billedOn(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y!, m!, 1)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** "created, 0.6 h awake", "0.6 h awake", "created". */
function serverLine(s: MyBilling['thisMonth']['servers'][number]): string {
  const hours = s.seconds > 0 ? `${(s.seconds / 3600).toFixed(1)} h awake` : '';
  return [s.created ? 'created' : '', hours].filter(Boolean).join(', ');
}

/** Under the credit left: what happens when it runs out. */
export function creditLine(left: number, hasCard: boolean): string {
  if (hasCard) return 'Used before your card is charged.';
  if (left < CREDIT_LOW_CENTS) return 'When it runs out, add a payment method to keep playing.';
  return "Your servers' time and fees come out of this first. With no card on file, you can't be charged.";
}

/** Opens one of Stripe's pages: Checkout (add a method, back to `returnTo`) or the customer portal. */
export async function openStripe(api: Api, page: 'card' | 'portal', returnTo?: string): Promise<void> {
  const { url } = await api.post<BillingLink>(`/v1/me/billing/${page}`, page === 'card' && returnTo ? { returnTo } : {});
  window.location.assign(url);
}

/**
 * Agrees to the current Terms, once their box is ticked: adding a card and creating a server need it.
 * `over18`: the Terms say only people 18 or older may add a payment method or own a server.
 */
export const acceptTerms = (api: Api) => api.post<void>('/v1/me/terms', { version: TERMS_VERSION, over18: true });

/** The Terms box (the canvas's consent board): unticked until they tick it; the Terms open in a new tab, keeping this page. */
export function TermsCheck({ checked, disabled, onChange, describedBy }: { checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void; describedBy?: string }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={describedBy} />
      <span>
        I agree to the{' '}
        <a href="/terms" target="_blank" rel="noreferrer">
          Terms
        </a>
        , including prices and refunds.
      </span>
    </label>
  );
}

export function BillingPage({ api, me }: { api: Api; me: MeResponse }) {
  const [billing, setBilling] = useState<MyBilling>();
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [redeemed, setRedeemed] = useState<RedeemedCredit>();
  // Stripe sends the browser back with `?card=saved` once a method was saved.
  const [saved] = useState(() => new URLSearchParams(globalThis.location?.search ?? '').get('card') === 'saved');

  useEffect(() => {
    api.get<MyBilling>('/v1/me/billing').then(setBilling, (err: unknown) => setError(errorText(err)));
  }, [api]);

  const redeem = () => {
    setBusy(true);
    setCodeError('');
    api
      .post<RedeemedCredit>('/v1/me/credit/redeem', { code })
      .then(async (done) => {
        setRedeemed(done);
        setCode('');
        setBilling(await api.get<MyBilling>('/v1/me/billing'));
      })
      .catch((err: unknown) => setCodeError(errorText(err)))
      .finally(() => setBusy(false));
  };

  const go = (page: 'card' | 'portal') => {
    setBusy(true);
    setError('');
    (async () => {
      if (page === 'card' && !me.termsAccepted) await acceptTerms(api);
      await openStripe(api, page);
    })().catch((err: unknown) => {
      setError(errorText(err));
      setBusy(false);
    });
  };
  return (
    <BillingView
      billing={billing}
      saved={saved}
      busy={busy}
      error={error}
      terms={me.termsAccepted ? undefined : { ticked, onTick: setTicked }}
      onAdd={() => go('card')}
      onPortal={() => go('portal')}
      redeem={{ code, onCode: setCode, onRedeem: redeem, error: codeError, done: redeemed }}
    />
  );
}

/** The page, given what's loaded (no fetching: easy to test). */
export function BillingView({ billing, saved, busy, error, terms, onAdd, onPortal, redeem }: {
  billing: MyBilling | undefined;
  saved: boolean;
  busy: boolean;
  error: string;
  /** Not yet agreed to the current Terms: their box, which adding a method needs ticked. */
  terms?: { ticked: boolean; onTick: (ticked: boolean) => void } | undefined;
  onAdd: () => void;
  onPortal: () => void;
  /** The code box: what's typed, the API's refusal, and the code just redeemed. */
  redeem?: { code: string; onCode: (code: string) => void; onRedeem: () => void; error: string; done?: RedeemedCredit | undefined } | undefined;
}) {
  const method = billing?.paymentMethod ?? null;
  const month = billing?.thisMonth;
  const credit = billing?.credit;
  const hasCredit = (credit?.left ?? 0) > 0;
  const fromCredit = credit?.thisMonth ?? 0;
  // Had credit, none left, no card: waking and creating stop until one's added.
  const usedUp = !!credit && !hasCredit && !method;
  const toPay = month ? month.cents - fromCredit : 0;
  return (
    <div className="billing">
      <a className="back" {...linkTo('/app')}>← Servers</a>
      <div className="page-head">
        <h1>Billing</h1>
        <p className="quiet">You pay for the servers you own: $5 to create one, then by the hour while it's awake. Billed on the 1st of each month.</p>
      </div>
      {error && <p className="notice" role="alert">{error}</p>}
      {billing?.pastDue && (
        <div className="billing-alert billing-alert-due" role="alert">
          <div>
            <strong>You have unpaid invoices</strong>
            <p>Your servers can't wake, and you can't create new ones, until it's paid. Update your payment method, then pay it.</p>
          </div>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={onPortal}>Pay now</button>
        </div>
      )}
      {saved && (
        <p className="billing-alert billing-alert-ok" role="status"><strong>Payment method saved.</strong></p>
      )}
      {redeem?.done && (
        <p className="billing-alert billing-alert-ok" role="status"><strong>{`Code ${redeem.done.code} added ${cents(redeem.done.credit)} of free credit. Have fun!`}</strong></p>
      )}
      {usedUp && (
        <div className="billing-alert billing-alert-add" role="alert">
          <div>
            <strong>Your free credit is used up</strong>
            <p>Add a payment method to keep waking and creating servers. Your worlds are safe in the meantime.</p>
          </div>
          <button className="btn btn-primary" type="button" disabled={busy || (terms && !terms.ticked)} onClick={onAdd}>Add a payment method</button>
        </div>
      )}
      {!billing ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : (
        <>
          <div className="billing-grid">
            <div className="billing-col">
            {credit && hasCredit && (
              <section className="panel billing-credit">
                <h2>Free credit</h2>
                <p className="billing-left">
                  <span className="billing-total">{cents(credit.left)}</span> <span className="quiet">{`left of ${cents(credit.granted)}`}</span>
                </p>
                <div className="meter" role="img" aria-label={`${cents(credit.left)} of ${cents(credit.granted)} free credit left`}>
                  <span style={{ width: `${Math.round((credit.left / credit.granted) * 100)}%` }} />
                </div>
                <p className="quiet">{creditLine(credit.left, !!method)}</p>
              </section>
            )}
            <section className="panel">
              <h2>Payment method</h2>
              {method ? (
                <>
                  <div className="billing-method">
                    <span className="billing-chip" aria-hidden="true">{chipText(method)}</span>
                    <div>
                      <strong>{methodText(method)}</strong>
                      <p className="quiet">Charged on the 1st of each month</p>
                    </div>
                  </div>
                  <div><button className="btn" type="button" disabled={busy} onClick={onPortal}>Change</button></div>
                </>
              ) : (
                <>
                  <p className="quiet">
                    {hasCredit
                      ? "Not needed while you have credit. Add one when you'd like to keep playing after it runs out."
                      : usedUp
                        ? 'Add one to keep playing.'
                        : 'No payment method yet. Add one to create servers.'}
                  </p>
                  {terms && <TermsCheck checked={terms.ticked} disabled={busy} onChange={terms.onTick} />}
                  <div>
                    <button className={hasCredit ? 'btn' : 'btn btn-primary'} type="button" disabled={busy || (terms && !terms.ticked)} onClick={onAdd}>
                      Add a payment method
                    </button>
                  </div>
                </>
              )}
              <p className="quiet billing-small">Payment details are handled by Stripe. Hearth never sees your card number.</p>
            </section>
            {redeem && !hasCredit && (
              <section className="panel">
                <h2><label htmlFor="credit-code">Have a code?</label></h2>
                <form
                  className="billing-code"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (redeem.code.trim()) redeem.onRedeem();
                  }}
                >
                  <input
                    id="credit-code"
                    className="field"
                    value={redeem.code}
                    autoComplete="off"
                    aria-invalid={redeem.error ? true : undefined}
                    aria-describedby={redeem.error ? 'credit-code-problem' : undefined}
                    onChange={(e) => redeem.onCode(e.target.value)}
                  />
                  <button className="btn" type="submit" disabled={busy || !redeem.code.trim()}>Redeem</button>
                  {redeem.error && <p id="credit-code-problem" className="field-problem" role="alert">{redeem.error}</p>}
                </form>
              </section>
            )}
            </div>
            {month && (
              <section className="panel billing-month">
                <h2>{`${monthOnly(month.month)} so far`}</h2>
                <span className="billing-total">{cents(toPay)}</span>
                {month.servers.length > 0 && (
                  <ul className="billing-rows">
                    {month.servers.map((s) => (
                      <li key={s.serverId}>
                        <span>{s.name} <span className="quiet">{`· ${serverLine(s)}`}</span></span>
                        <span>{cents(s.cents)}</span>
                      </li>
                    ))}
                    {fromCredit > 0 && (
                      <>
                        <li className="billing-credit-row"><span>Paid from free credit</span><span>{`−${cents(fromCredit)}`}</span></li>
                        <li className="billing-pay-row"><span>To pay</span><span>{cents(toPay)}</span></li>
                      </>
                    )}
                  </ul>
                )}
                <p className="quiet">
                  {!method && fromCredit > 0 ? 'Nothing to pay: no card is on file, so nothing can be charged.' : `Billed on ${billedOn(month.month)}.`}
                </p>
              </section>
            )}
          </div>
          {(method || billing.pastDue) && (
            <section className="panel billing-invoices">
              <div>
                <h2>Past invoices</h2>
                <p className="quiet">Every invoice and receipt, kept by Stripe.</p>
              </div>
              <button className="btn" type="button" disabled={busy} onClick={onPortal}>View invoices</button>
            </section>
          )}
        </>
      )}
    </div>
  );
}
